import { TrendingUp } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { EmptyState } from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import type { TopSellerItem } from "@/types";

interface BestSellersCardProps {
  items: TopSellerItem[];
}

export function BestSellersCard({ items }: BestSellersCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Best sellers
        </CardTitle>
        <CardDescription>Top 10 by quantity sold</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title="No sales yet"
            description="Sold items will appear here"
          />
        ) : (
          <ol className="space-y-2">
            {items.map((item, idx) => (
              <li
                key={`${item.productId}-${idx}`}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {idx + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{item.productName}</p>
                    {item.variantName && (
                      <p className="truncate text-xs text-muted-foreground">
                        {item.variantName}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{item.totalQuantity}× sold</p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(item.totalRevenue)} revenue
                  </p>
                  <p className="text-xs text-green-600">
                    {formatCurrency(item.totalProfit)} profit
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
